import { staticImplements } from 'utils/types'
import type { Availability, CheckCouponOn, CouponFlowDependencies, CouponToApply } from './types'
import type { Coupon, Channel, OrderType } from 'shared-types/Coupon'

import Home from 'mobx/Home'
import User from 'mobx/User'
import Infra from 'mobx/Infra'
import Account from 'mobx/Account'
import type { NextRouter } from 'next/router'
import type CouponsStore from 'mobx/Coupons'
import { sendRequest } from 'utils/utils'
import { isAvailableNow } from '@tictuk/item-availability'
import { isCouponForChannel } from './utils'
import CouponError, { ErrorCode } from 'mobx/Coupons/errors'
import type CartStore from 'mobx/Cart/store'
import type AddressManagerV2Store from 'mobx/AddressManagerV2/store'
import { StoreStatus } from 'mobx/AddressManager/types'
import type { Props as DialogBoxProps } from 'components/common/DialogBox'
import type { Menu } from 'types/Menu'
import type { StoreMetaData } from 'types/StoreContext'
import type { ShowSnackbarProps } from 'mobx/Infra/Infra.type'
import { storeUrls } from 'utils/api/storefrontUrls'
import { CONSTANTS } from 'utils/constants'

@staticImplements<CouponFlowDependencies>()
export default class CouponFlowRepository {
	static router: NextRouter | null = null

	static couponsStore: CouponsStore | null = null

	static cartStore: CartStore | null = null

	static addressManagerV2Store: AddressManagerV2Store | null = null

	static setNotification: ((notification: Omit<DialogBoxProps, 'okText'> & Partial<Pick<DialogBoxProps, 'okText'>>) => void) | null = null

	static closeNotification: ((removeStateFromHistory?: boolean, closeEvenIfPermanent?: boolean) => void) | null = null

	static areAllStoresClosed: (() => boolean) | null = null

	static getTranslatedTextByKey: (path: string, fallback?: string) => string

	static setRouter(router: NextRouter): void {
		this.router = router
	}

	static setCouponsStore(couponsStore: CouponsStore): void {
		this.couponsStore = couponsStore
	}

	static setCartStore(cartStore: CartStore): void {
		this.cartStore = cartStore
	}

	static setAddressManagerV2Store(addressManagerV2Store: AddressManagerV2Store): void {
		this.addressManagerV2Store = addressManagerV2Store
	}

	static setSetNotification(
		setNotification: (notification: Omit<DialogBoxProps, 'okText'> & Partial<Pick<DialogBoxProps, 'okText'>>) => void
	): void {
		this.setNotification = setNotification
	}

	static setCloseNotification(closeNotification: (removeStateFromHistory?: boolean, closeEvenIfPermanent?: boolean) => void): void {
		this.closeNotification = closeNotification
	}

	static setAreAllStoresClosed(areAllStoresClosed: () => boolean): void {
		this.areAllStoresClosed = areAllStoresClosed
	}

	static setGetTranslatedTextByKey(getTranslatedTextByKey: (path: string, fallback?: string) => string): void {
		this.getTranslatedTextByKey = getTranslatedTextByKey
	}

	static setCouponModal(coupon: Coupon | null): void {
		if (!this.couponsStore) {
			throw new Error('Coupons store is not set')
		}

		this.couponsStore?.setCouponModal(coupon)
	}

	static showError(props: {
		message: string
		title?: string
		cancelText?: string
		okText?: string
		showCancelButton?: boolean
		okAction?: () => void
		cancelAction?: () => void
	}): void {
		if (!CouponFlowRepository.setNotification) {
			return
		}

		CouponFlowRepository.setNotification?.({
			open: true,
			title: props.title || CouponFlowRepository.getTranslatedTextByKey('webviewFlow.error'),
			message: props.message,
			cancelText: props.cancelText,
			okText: props.okText,
			showCancelButton: !!props.showCancelButton,
			onClose: () => {
				this.closeNotification?.()
			},
			okAction: () => {
				props.okAction?.()
				this.closeNotification?.()
			},
			cancelAction: () => {
				props.cancelAction?.()
				this.closeNotification?.()
			},
		})
	}

	static async getOrderType(): Promise<number | string | undefined> {

		if(CouponFlowRepository.addressManagerV2Store?.newLocalizationFlowEnabled && CouponFlowRepository.addressManagerV2Store.alreadyLocalized) {
			return CouponFlowRepository.addressManagerV2Store.actualFlow?.orderTypeFlow?.orderType
		}

		if(CouponFlowRepository.addressManagerV2Store?.newLocalizationFlowEnabled && !CouponFlowRepository.addressManagerV2Store.alreadyLocalized) {
			const orderTypeFlows = await CouponFlowRepository.addressManagerV2Store.getFilteredOrderTypeFlows()
			const firstOrderTypeFlows = orderTypeFlows && orderTypeFlows.length ? orderTypeFlows[0] : null
			
			return firstOrderTypeFlows?.orderType
		}

		return User.getOrderType()
	}

	static showSnackbar({ key, snackId, message, status, isAttachedToElement = true, duration = 3000 }: ShowSnackbarProps): void {
		Infra.showSnackbar({ key, snackId, message, status, isAttachedToElement, duration })
	}

	static showConfirm(message: string, okAction: () => void, cancelAction?: () => void): void {
		if (!CouponFlowRepository.setNotification) {
			return
		}

		CouponFlowRepository.setNotification?.({
			open: true,
			message,
			cancelAction,
			okAction,
			onClose: () => {
				cancelAction?.()
				this.closeNotification?.()
			},
		})
	}

	static getCoupon(code: string, checkCouponOn: CheckCouponOn): Promise<Coupon | null> {
		if (!this.couponsStore) {
			throw new CouponError('Coupons store is not set', ErrorCode.COUPON_NOT_FOUND)
		}
		return this.couponsStore.getCoupon(code, checkCouponOn)
	}

	static assertCouponAvailable(coupon: Coupon, { date, channel, orderType }: { date: Date; channel: Channel; orderType: OrderType }): void {
		if (!this.couponsStore) {
			throw new CouponError('Coupons store is not set', ErrorCode.COUPON_NOT_FOUND)
		}

		this.couponsStore.assertCouponAvailable(coupon, { date, channel, orderType })
	}

	static isLocalized(): boolean {
		if (!CouponFlowRepository.addressManagerV2Store) {
			throw new Error('AddressManagerV2 store is not set')
		}

		if(CouponFlowRepository.addressManagerV2Store.newLocalizationFlowEnabled) {
			return CouponFlowRepository.addressManagerV2Store.alreadyLocalized || false
		}

		return CouponFlowRepository.addressManagerV2Store.isUserLocalized()
	}

	static resetLocalization(): void {
		if (!CouponFlowRepository.addressManagerV2Store) {
			throw new Error('AddressManagerV2 store is not set')
		}
		CouponFlowRepository.addressManagerV2Store.resetLocalization()
	}

	static getAndUpdateSession(): Promise<
		| {
				menuRes: Menu | null
				storeMetaData: StoreMetaData | null
				serverSession: unknown
				menuUrl: string
		  }
		| undefined
	> | undefined {
		if (!this.cartStore) {
			throw new Error('Cart store is not set')
		}
		if (!CouponFlowRepository.addressManagerV2Store) {
			throw new Error('AddressManagerV2 store is not set')
		}

		if(CouponFlowRepository.addressManagerV2Store.newLocalizationFlowEnabled) {
			const menuPath = CouponFlowRepository.addressManagerV2Store.getLocalizedMenuPath()

			if(!menuPath) {
				throw new Error('menuPath is not set')
			}

			return new Promise((resolve) => {
				resolve({
					menuRes: null,
					storeMetaData: null,
					serverSession: null,
					menuUrl: menuPath
				})
			})
		}

		return CouponFlowRepository.addressManagerV2Store.onMenuClickWhenLocalized(this.cartStore, User)
	}

	// Returns true if the localization header can be opened on the page
	static isPageLegal(currentPath: string): boolean {
		const legalPagesRegex = [
			/^\/$/,
			/^\/home$/,
			/^\/[^/]+\/deals$/,
			// /^\/[^\/]+\/menu$/, REMOVED BECAUSE OF A BLACK OVERLAY ON THIS PAGE ONLY - needs to be fixed
			/^\/[^/]+\/menu\/.*$/,
			/^\/[^/]+\/coupons$/,
		]

		for (const regex of legalPagesRegex) {
			if (regex.test(currentPath)) {
				return true
			}
		}

		return false
	}

	static openLocalizationModal(couponToApply: CouponToApply): void {
		if(CouponFlowRepository.addressManagerV2Store?.newLocalizationFlowEnabled) {
			CouponFlowRepository.addressManagerV2Store?.start()
			return
		}

		Home.handleItemSelectedMode(couponToApply, User)
	}

	static async isStoreClosed(): Promise<boolean> {
		if (!CouponFlowRepository.addressManagerV2Store) {
			throw new Error('AddressManagerV2 store is not set')
		}
		const localizedStoreId = CouponFlowRepository.addressManagerV2Store.getLocalizedStoreId()

		if (localizedStoreId) {
			const urls = await storeUrls(localizedStoreId)
			const { status, availability } = (await sendRequest(
				false,
				`${urls.storeDetailsUrl}&channel=${CONSTANTS.CHANNEL_TYPE.WEB}&$pick=availability,status`,
				'GET',
				null,
				null,
				true,
				90000,
				null,
				false
			)) as {
				status: StoreStatus
				availability: Availability
			}

			if (!availability) {
				return true
			}

			if (status !== StoreStatus.Active) {
				return true
			}

			return !isAvailableNow(availability)
		}

		return false
	}

	static isChainClosed(): boolean {
		if (!CouponFlowRepository.areAllStoresClosed) {
			return true
		}

		return CouponFlowRepository.areAllStoresClosed()
	}

	static isCouponForChannel(couponChannels: Channel[]): boolean {
		return isCouponForChannel(couponChannels)
	}

	static isUserLoggedIn() {
		return Account?.isAuthenticated() ?? false
	}

	static async removeDiscount(couponCode: string): Promise<void> {
		if (!this.cartStore) {
			throw new Error('Cart store is not set')
		}
		try {
			await this.cartStore.removeCoupon(couponCode)
		} catch (e) {
			console.error(`CouponFlowRepository removeDiscount - error : ${e}`)
		}
	}
}
